import { useEffect, useRef, useState } from "react";
import { MapPin, Loader2, MapPinOff } from "lucide-react";
import api from "../../lib/api";
import { toast } from "sonner";
import { useAuth } from "../../contexts/AuthContext";

/**
 * Iter 77 — Rental visibility toggle.
 *
 * The driver flips themselves between "Connecté" (vehicle visible on
 * passenger maps, GPS pushed every 10 s) and "Déconnecté" (vehicle
 * hidden, GPS record purged server-side).
 *
 * GPS permission is MANDATORY to go online: refusing the browser
 * prompt keeps the driver offline, as per the Trigo Tour spec.
 */
const PUSH_INTERVAL_MS = 10_000;

export default function DriverVisibilityToggle() {
  const { user } = useAuth();
  const [online, setOnline] = useState(!!user?.is_online);
  const [busy, setBusy] = useState(false);
  const watchIdRef = useRef(null);
  const intervalRef = useRef(null);
  const lastSentRef = useRef(0);

  // Mirror the server state on mount — the user prop isn't refreshed on
  // every toggle, so pull the authoritative flag from `/auth/me`.
  useEffect(() => {
    let cancelled = false;
    api.get("/auth/me").then(({ data }) => {
      if (!cancelled) setOnline(!!data.is_online);
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [user?.id]);

  // Resume the GPS push loop automatically after a page reload if the
  // driver is already marked online server-side (prevents the common
  // "I'm online but the admin says I never updated my location" bug).
  useEffect(() => {
    if (online) startGpsPush();
    else stopGpsPush();
    return stopGpsPush;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [online]);

  const pushLocation = async (coords) => {
    const now = Date.now();
    if (now - lastSentRef.current < PUSH_INTERVAL_MS - 1500) return;
    lastSentRef.current = now;
    try {
      await api.post("/location/update", {
        lat: coords.latitude,
        lng: coords.longitude,
      });
    } catch { /* transient network error — the next tick will retry */ }
  };

  const startGpsPush = () => {
    if (!navigator.geolocation) return;
    stopGpsPush();
    watchIdRef.current = navigator.geolocation.watchPosition(
      (pos) => pushLocation(pos.coords),
      () => { /* silent — stale push cycle will catch up */ },
      { enableHighAccuracy: true, maximumAge: 5000, timeout: 15000 },
    );
    // Belt-and-braces: some mobile browsers suspend `watchPosition` when
    // the tab goes idle, so we also fire a `getCurrentPosition` every
    // 10 s to keep the server-side freshness window alive.
    intervalRef.current = setInterval(() => {
      navigator.geolocation.getCurrentPosition(
        (pos) => pushLocation(pos.coords),
        () => {},
        { enableHighAccuracy: true, maximumAge: 5000, timeout: 15000 },
      );
    }, PUSH_INTERVAL_MS);
  };

  const stopGpsPush = () => {
    if (watchIdRef.current != null) {
      try { navigator.geolocation.clearWatch(watchIdRef.current); } catch { /* noop */ }
      watchIdRef.current = null;
    }
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  };

  const requestPositionOnce = () => new Promise((resolve, reject) => {
    if (!navigator.geolocation) return reject(new Error("GPS indisponible"));
    navigator.geolocation.getCurrentPosition(
      (pos) => resolve({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
      (err) => reject(err),
      { enableHighAccuracy: true, timeout: 15000 },
    );
  });

  const handleToggle = async () => {
    if (busy) return;
    setBusy(true);
    try {
      if (!online) {
        // Going ONLINE — must have GPS permission + a fresh fix first.
        let fix;
        try {
          fix = await requestPositionOnce();
        } catch {
          toast.error("Activation GPS requise", {
            description: "Autorisez la géolocalisation dans votre navigateur pour vous connecter.",
          });
          setBusy(false);
          return;
        }
        await api.post("/driver/visibility", { online: true, lat: fix.lat, lng: fix.lng });
        setOnline(true);
        toast.success("Vous êtes connecté", {
          description: "Votre véhicule est visible pour les passagers à proximité.",
        });
      } else {
        await api.post("/driver/visibility", { online: false });
        setOnline(false);
        toast("Vous êtes déconnecté", {
          description: "Votre véhicule n'est plus visible pour les passagers.",
        });
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    } finally {
      setBusy(false);
    }
  };

  return (
    <button
      onClick={handleToggle}
      disabled={busy}
      className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-xs font-semibold transition-all ${
        online
          ? "bg-emerald-500 text-white border-emerald-500 shadow"
          : "bg-white text-[#0A2540] border-[#CBD5E1] hover:bg-[#F8FAFC]"
      }`}
      title={online ? "Vous êtes visible pour les passagers" : "Activez pour être visible"}
      data-testid="driver-visibility-toggle"
    >
      {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : online ? (
        <MapPin className="w-3.5 h-3.5 animate-pulse" />
      ) : (
        <MapPinOff className="w-3.5 h-3.5" />
      )}
      <span>{online ? "Connecté" : "Déconnecté"}</span>
    </button>
  );
}
